import styled from 'styled-components/macro'

import { Button, DotsLoader, Icon, Layout, Text, Tooltip } from '@loadsmart/miranda-react'

import { type AgnosticDocument, DocumentStatus, type DocumentType } from '../../domain/Document'
import { type InvoiceStatus } from '../../domain/Invoice'
import {
  formatDocumentDate,
  formatDocumentValue,
  getDocumentTitleProps,
} from '../../presenters/documentPresenter'

type BaseDocumentItemProps = Readonly<AgnosticDocument> & {
  readonly onClick: () => void
  readonly loading?: boolean
  readonly disabled?: boolean
  readonly subtitle?: string
}

type InvoiceDocumentItemProps = BaseDocumentItemProps & {
  readonly status: InvoiceStatus
  readonly type: DocumentType.Invoice
}

type OtherDocumentItemProps = BaseDocumentItemProps & {
  readonly status?: DocumentStatus | null
  readonly type:
    | DocumentType.BOL
    | DocumentType.POD
    | DocumentType.ProofOfPickup
    | DocumentType.Other
    | DocumentType.RateConfirmation
    | DocumentType.WeightCertificate
    | DocumentType.InspectionCertificate
}

export type DocumentItemProps = OtherDocumentItemProps | InvoiceDocumentItemProps

const StyledText = styled(Text)<{ lineThrough: boolean }>`
  --m-text-max-width: 10rem;
  ${({ lineThrough }) =>
    lineThrough &&
    `
    text-decoration: line-through;
  `}
`

const StyledGroup = styled(Layout.Group)`
  flex-wrap: nowrap;
`

export function DocumentItem({
  title,
  createdAt,
  onClick,
  type,
  status,
  loading = false,
  subtitle,
}: DocumentItemProps) {
  const titleProps = getDocumentTitleProps(status)
  const isDenied = status === DocumentStatus.Denied
  const formattedDate = formatDocumentDate(createdAt)

  return (
    <StyledGroup justify="space-between" align="center">
      <Layout.Group justify="space-between">
        <Layout.Stack gap="spacing-1">
          <Layout.Group align="center" gap="spacing-0-5">
            <Icon
              data-testid="document-icon-file"
              name="file"
              size="small"
              color="color-neutral-100"
            />
            <StyledText variant="body-md-bold" ellipsized lineThrough={isDenied} {...titleProps}>
              {formatDocumentValue(title, type)}
            </StyledText>
          </Layout.Group>
          {subtitle && (
            <Text variant="body-sm" color="color-neutral-60">
              {subtitle}
            </Text>
          )}
          {formattedDate && (
            <Text variant="body-sm" color="color-neutral-60">
              {formattedDate}
            </Text>
          )}
        </Layout.Stack>
      </Layout.Group>
      <Layout.Group justify="flex-end" gap="spacing-4" align="center">
        <Layout.Stack>
          <Tooltip message="Preview" placement="top">
            <Button
              data-testid={`view-document-${type}`}
              aria-label={`view document ${type}`}
              variant="icon-secondary"
              size="small"
              onClick={onClick}
              loading={loading}
            >
              {loading ? (
                <DotsLoader data-testid={`loading-document-${type}`} size="4px" />
              ) : (
                <Icon size="small" name="eye" />
              )}
            </Button>
          </Tooltip>
        </Layout.Stack>
      </Layout.Group>
    </StyledGroup>
  )
}
